import { Link } from 'react-router-dom'
import { Card } from '@winnin/winnin_ui.components.card'
import { Title } from '@winnin/winnin_ui.components.title'

import CardMetricVariation from 'components/CardMetricVariation'
import LineChart from 'components/LineChart'
import Thumbnail from 'components/Thumbnail'
import TopicListSmall from 'components/TopicListSmall'
import { PROVIDERS } from 'consts'
import { T } from 'lib/tolgee'
import { Chart, numberUtils, stringUtils, countries, makeUrlTo, humanizeLargeNumber } from 'utils'
import { getFormatedTooltipCategoriesChart } from 'utils/getFormatedTooltipCategoriesChart'

import { chartOptions } from './consts'
import AddToCreatorCheckbox from '../AddToCreatorCheckbox'
import VideoHighlight from '../VideoHighlight'

function getTierBySubscribers(subs) {
  const numberOfSubs = Number(subs)

  if (numberOfSubs > 5_000_000) return 'icon'
  if (1_000_000 < numberOfSubs && numberOfSubs <= 5_000_000) return 'top'
  if (200_000 < numberOfSubs && numberOfSubs <= 1_000_000) return 'mid'
  if (numberOfSubs <= 200_000) return 'micro'
}

function getTierLabel(tier) {
  switch (tier) {
    case 'icon':
      return '5M+'
    case 'top':
      return '1M to 5M'
    case 'mid':
      return '200K to 1M'
    default:
    case 'micro':
      return 'Up to 200K'
  }
}

const TITLE_LENGTH_LIMIT = 48
const TOLGEE_PREFIX = 'CreatorCard_CardComponent'

const extractSeriesFromPerformance = (key, performance) => {
  return [
    {
      data: performance.map(p => p[key]),
      name: key,
    },
  ]
}

function CardComponent({
  id,
  index,
  title,
  country,
  thumbnail,
  provider,
  groupId,
  subscribers,
  subscribersVariation,
  subscribersLabel,
  isTrending,
  isBrandGroup,
  hideBrandTag,
  hideTier,
  topics,
  performance,
  highlight,
  topicMetris,
  mostLikedVideo,
  titleComponent,
  isBrand,
  onCreatorClickCallback,
  noTopicLink,
  isPublic = false,
}) {
  const providerName = PROVIDERS[provider]?.NAME
  const creatorGroupType = isBrand ? 'Brand Group' : 'Creator Group'
  const followerOrSubscribersLabel =
    provider === 'tw' ? (
      <T keyName={`${TOLGEE_PREFIX}-metricFollowers`}>Followers</T>
    ) : (
      <T keyName={`${TOLGEE_PREFIX}-metricSubscribers`}>Subscribers</T>
    )
  const tier = getTierBySubscribers(subscribers)
  const tierLabel = getTierLabel(tier)
  const categories = performance && Chart.buildPerformanceCategories(performance)
  const formatedCategories = getFormatedTooltipCategoriesChart(categories)

  const charts = performance && {
    views: {
      categories: formatedCategories,
      performance: extractSeriesFromPerformance('views', performance),
    },
    likes: {
      categories: formatedCategories,
      performance: extractSeriesFromPerformance('likes', performance),
    },
  }

  const verifyViews = provider === 'fb'

  function handleCreatorClick() {
    onCreatorClickCallback?.({ id, title, provider, groupId, index })
  }

  return !highlight ? (
    <div className="CreatorCard-inside-container" onClick={handleCreatorClick}>
      <div className="CreatorCard-first-column">
        {!isPublic && (
          <AddToCreatorCheckbox id={id} provider={provider} title={title} thumbnail={thumbnail} groupId={groupId} />
        )}
        <div className="thumbnail-container">
          <Thumbnail
            provider={providerName?.toLowerCase()}
            trending={isTrending}
            alt={title}
            src={thumbnail}
            className="thumbnail"
            width="84"
            height="84"
          />
        </div>
      </div>

      <div className="CreatorCard-second-column">
        {!titleComponent ? (
          <>
            <div className="title">
              <span title={title}>{stringUtils.truncate(title, TITLE_LENGTH_LIMIT)}</span>
            </div>

            <div className="CreatorCard-brand-country-container">
              {isBrand && !isBrandGroup && !hideBrandTag && (
                <div className="CreatorCard-brand">
                  <span>Brand</span>
                </div>
              )}

              {isBrandGroup && !hideBrandTag && (
                <div
                  aria-label="This Brand is a Facebook Page grouping of differents countries, which share the same metrics."
                  className="tooltipped tooltipped-n tooltipped-multiline CreatorCard-brand"
                >
                  <span>{creatorGroupType}</span>
                </div>
              )}

              {(isBrand || isBrandGroup) && country && !hideBrandTag && (
                <span className="CreatorCard-brand-country-separator"> - </span>
              )}

              {country && <div className="CreatorCard-country">{countries.byValue(country).NAME}</div>}
            </div>
          </>
        ) : (
          titleComponent
        )}

        <TopicListSmall noLink={noTopicLink || isPublic} tooltippedText="Creator Topic" topics={topics} />

        <div className="CreatorCard-subscribers-content">
          <div className="CreatorCard-subscribers">
            <div className="CreatorCard-small-title">
              {subscribers !== null ? numberUtils.format(Number.parseInt(subscribers, 10), 0) : '-'}
            </div>
            <div className="CreatorCard-subtitle">
              {' '}
              {subscribersLabel ? subscribersLabel : followerOrSubscribersLabel}{' '}
            </div>
          </div>
          <CardMetricVariation metricVariation={subscribersVariation} id={index} />
          {!hideTier && (
            <div className="CreatorCard-tier">
              <div className="CreatorCard-small-title">{tier}</div>
              <div className="CreatorCard-subtitle">
                <T keyName={`${TOLGEE_PREFIX}-metricTier`}>Tier</T>
              </div>

              <Card className="tier-details-popover">
                <Title tag="h5" weight="regular">
                  <T keyName={`${TOLGEE_PREFIX}-cardHeaderTier`}>Creator Tier*</T>
                </Title>
                <div className="tier-details-group">
                  <span className="tier-tag">{tier}</span>
                  <Title weight="regular" tag="h6">
                    <T keyName={`${TOLGEE_PREFIX}-cardHeaderTierSubheader`} parameters={{ tierLabel }}>
                      {'{tierLabel} Subscribers'}
                    </T>
                  </Title>
                </div>
                <p className="tier-observation">
                  <T keyName={`${TOLGEE_PREFIX}-cardHeaderTierobs`}>
                    *An interpretive classification based on the number of subscribers.
                  </T>
                </p>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="CreatorCard-highlight">
      <div className="CreatorCard-highlight-left">
        <div className="CreatorCard-inside-container">
          <div className="CreatorCard-first-column">
            <AddToCreatorCheckbox id={id} provider={provider} title={title} thumbnail={thumbnail} groupId={groupId} />
            <div className="thumbnail-container highlight">
              <Link
                to={makeUrlTo.creator({ groupId, provider, creatorId: id })}
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                <Thumbnail
                  provider={providerName.toLowerCase()}
                  trending={isTrending}
                  alt={title}
                  src={thumbnail}
                  className="thumbnail"
                  width="100"
                  height="100"
                />
              </Link>
            </div>
          </div>
        </div>
        <Link
          to={makeUrlTo.creator({ groupId, provider, creatorId: id })}
          onClick={e => {
            e.stopPropagation()
          }}
          className="CreatorCard-highlight-left-h3"
        >
          {stringUtils.truncate(title, TITLE_LENGTH_LIMIT)}
        </Link>
        <p>
          <T
            keyName={`${TOLGEE_PREFIX}-mostRelevantCreator`}
            parameters={{ likes: humanizeLargeNumber(topicMetris?.totalLikes), videos: topicMetris?.videos }}
          >
            {
              'is the most relevant creator with {likes} likes in this topic and {videos} videos published in this topic in the last 3 months'
            }
          </T>
        </p>
        <div className="CreatorCard-second-column">
          <div className="CreatorCard-subscribers-content">
            <div className="CreatorCard-subscribers">
              <div className="CreatorCard-body-text semibold">
                {subscribers !== null ? numberUtils.format(Number.parseInt(subscribers, 10), 0) : '-'}
              </div>
              <div className="CreatorCard-subtitle">
                {' '}
                {subscribersLabel ? subscribersLabel : followerOrSubscribersLabel}{' '}
              </div>
            </div>
            <CardMetricVariation metricVariation={subscribersVariation} id={index} />
            {!hideTier && (
              <div className="CreatorCard-tier">
                <div className="body2 semibold">{tier}</div>
                <div className="CreatorCard-subtitle">
                  <T keyName={`${TOLGEE_PREFIX}-metricTier`}>Tier</T>
                </div>

                <Card className="tier-details-popover">
                  <Title tag="h5" weight="regular">
                    <T keyName={`${TOLGEE_PREFIX}-cardHeaderTier`}>Creator Tier*</T>
                  </Title>
                  <div className="tier-details-group">
                    <span className="tier-tag">{tier}</span>
                    <Title weight="regular" tag="h6">
                      <T keyName={`${TOLGEE_PREFIX}-cardHeaderTierSubheader`} parameters={{ tierLabel }}>
                        {'{tierLabel} Subscribers'}
                      </T>
                    </Title>
                  </div>
                  <p className="tier-observation">
                    <T keyName={`${TOLGEE_PREFIX}-cardHeaderTierObs`}>
                      *An interpretive classification based on the number of subscribers.
                    </T>
                  </p>
                </Card>
              </div>
            )}
          </div>
        </div>
        <div className="CreatorCard-highlight-charts">
          <div className="CreatorCard-highlight-charts-likes">
            <p className="caption semibold">
              <T keyName={`${TOLGEE_PREFIX}-captionPerformanceTime`}>Last 12 months performance:</T>
            </p>
            <LineChart
              series={charts.likes.performance}
              options={{ ...chartOptions, xaxis: { categories: charts.likes.categories }, legend: { show: false } }}
              width={100}
              height={40}
            />
            <p className="caption">
              <T keyName={`${TOLGEE_PREFIX}-captionPerformanceLikes`}>Cumulative Likes performance</T>
            </p>
          </div>
          {!verifyViews && (
            <div className="CreatorCard-highlight-charts-views">
              <LineChart
                series={charts.views.performance}
                options={{ ...chartOptions, xaxis: { categories: charts.views.categories }, legend: { show: false } }}
                width={100}
                height={40}
              />
              <p className="caption">
                <T keyName={`${TOLGEE_PREFIX}-captionPerformanceViews`}>Cumulative Views performance</T>
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="CreatorCard-highlight-right">
        <VideoHighlight mostLikedVideo={mostLikedVideo} title={title} />
      </div>
    </div>
  )
}

export default CardComponent
